import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Component, HostListener, Inject, OnInit, ViewChild} from '@angular/core';
import {WorkbenchViewComponent} from '../../../shared/components/viewcontainers/workbench-view.component';
import {WORKBENCH_VIEW_CONFIG, WorkbenchContext, WorkbenchViewConfig} from '../../../shared/types';
import {ConsoleSessionId, DeviceId} from '@webmate-web-core/shared/types/ids';
import {WorkbenchLayoutManager} from '../../../shared/services/workbench-layout-manager.service';
import {WorkbenchContextService} from '../../../shared/services/workbench-context.service';
import {DeviceService} from '@webmate-web-core/shared/services/device/device.service';
import {combineLatest, of, Subject} from 'rxjs';
import {applyFilter, LogFilter, LogMessage} from '../../console/services/log-message.interface';
import {CdkVirtualScrollViewport} from '@angular/cdk/scrolling';
import {ConfigPersistenceService} from '../../console/services/config-persistence.service';
import {getDataTestingIdForPluginType} from '../../../shared/utils/console-util';
import {ToolbarPluginType} from '../../console/services/toolbar-plugin.interface';
import {ConsoleSession} from '@webmate-web-core/shared/types/console-session';
import {ConsoleSessionService} from '@webmate-web-core/shared/services/device/console-session.service';
import {faLongArrowDown} from '@fortawesome/pro-solid-svg-icons';
import {distinctUntilChanged, map, startWith, switchMap, take} from 'rxjs/operators';
import {isDefined} from "@webmate-web-core/shared/utils/utils";
import {OpenSTFService} from '../../console/services/providers/openstf.service';
import {AndroidLogLevels, IOSLogLevels} from '../../../../webmate/shared/console/types/shared';
import {DevicePropertyNames} from "@webmate-web-core/shared/types/devices/device-properties";



@UntilDestroy()

@Component({
    templateUrl: './log-view.component.html',
    styleUrls: ['./log-view.component.less']
})

export class LogViewComponent extends WorkbenchViewComponent implements OnInit {

    workbenchContext: WorkbenchContext;
    deviceId: DeviceId;
    deviceSerial: string;
    sessionId: ConsoleSessionId;
    isMobileDevice: boolean=false;
    activeConsole: boolean=false;
    consoleSession: ConsoleSession;
    toolbarPluginType: ToolbarPluginType=ToolbarPluginType.LOG;

    contentHeight: number;
    itemSize: number = 15;
    padding: number;


    private filterChanged: Subject<void>=new Subject<void>();
    stickToBottom=true;
    filteringEnabled: boolean=true;
    filter: LogFilter={};
    supportedLogTypes: any[];
    currentMessages: LogMessage[];
    deviceName: string;
    logSource: string;


    @ViewChild(CdkVirtualScrollViewport) virtualScrollViewport: CdkVirtualScrollViewport;

    longArrowDown = faLongArrowDown;

    constructor(layoutManager: WorkbenchLayoutManager,
                @Inject(WORKBENCH_VIEW_CONFIG) public config: WorkbenchViewConfig<any>,
                private readonly workbenchContextService: WorkbenchContextService,
                private readonly consoleSessionService: ConsoleSessionService,
                private deviceService: DeviceService,
                private configService: ConfigPersistenceService,
                private openSTFService: OpenSTFService,
    ) {
        super(layoutManager, config);
    }

    public getDataTestingIdForPluginType: (t: ToolbarPluginType, prefix?: string, suffix?: string) => string = getDataTestingIdForPluginType;

    ngOnInit() {
        this.workbenchContextService.subscribeWorkbenchContext$().pipe(
            switchMap(context => {
                this.workbenchContext = context;
                this.activeConsole = !!context.deviceContext?.activeConsole?.consoleSession;
                this.sessionId = this.workbenchContext.deviceContext?.activeConsole?.consoleSession;
                this.deviceId = context.deviceContext?.activeDevice;
                if (this.deviceId) {
                    return this.deviceService.getDevice$(this.deviceId).pipe(take(1));
                }
                else {
                    return of(undefined);
                }
            }),
            switchMap(device => {
                if (device) {
                    const platformType = device.platform?.platformType;
                    if (platformType) {
                        this.isMobileDevice = ['Android', 'iOS'].includes(platformType);
                    } else {
                        this.isMobileDevice = false;
                    }
                    this.deviceSerial = device.properties?.[DevicePropertyNames.OpenSTFSerial] ?? null;
                }
                if (this.sessionId) {
                    return this.consoleSessionService.getConsoleSession$(this.sessionId).pipe(take(1));
                } else {
                    return of(undefined);
                }
            }),
            switchMap(session => {
                if (session && this.deviceSerial) {
                        this.consoleSession = session;
                        this.stickToBottom = this.configService.getCustomData(session.providerType, this.toolbarPluginType, 'stickToBottom') ?? true;
                        this.filteringEnabled = this.configService.getCustomData(session.providerType, this.toolbarPluginType, 'filterEnabled') ?? false;
                        return this.openSTFService.getDevice(this.consoleSession.id, this.deviceSerial).pipe(take(1));
                } else {
                    this.sessionId = undefined;
                    return of(undefined);
                }
            }),
            switchMap(openSTFDevice => {
                if (openSTFDevice){
                    this.logSource = openSTFDevice.platform;
                    this.deviceName = openSTFDevice.name;
                    this.changeHeight();
                }
                //TODO: support filter storage, but make it log source dependent
                this.filter = {};
                if (this.logSource) {
                    switch (this.logSource) {
                        case 'Android':
                            this.supportedLogTypes=AndroidLogLevels.map(level => {
                                return {value: level, label: level};
                            });
                            break;
                        case 'iOS':
                            this.supportedLogTypes=IOSLogLevels.map(level => {
                                return {value: level, label: level};
                            });
                            break;
                        default:
                            console.debug('unknown log source: ', this.logSource);
                    }
                    if (this.consoleSession.id && openSTFDevice) {
                        const logStreamSubject = this.openSTFService.startLogStream(this.consoleSession.id, openSTFDevice);
                        return combineLatest([
                            logStreamSubject,
                            this.filterChanged.asObservable().pipe(startWith(void 0))
                        ]).pipe(
                            map(([logs]) => logs),
                            distinctUntilChanged(),
                            map(msgs => {
                                if (this.filteringEnabled && isDefined(this.filter)) {
                                    return applyFilter(msgs, this.filter);
                                } else {
                                    return msgs;
                                }
                            }),
                        );
                    } else {
                        return of(undefined);
                    }
                } else {
                    return of(undefined);
                }
            }),
            untilDestroyed(this),
        ).subscribe(msgs => {
            if (msgs) {
                this.currentMessages = msgs;
                if (this.stickToBottom) {
                    this.scrollToBottom();
                }
            }
        })
    }

    @HostListener('window:resize', ['$event'])
    private changeHeight(){
        if (this.isMobileDevice) {
            const div = document.getElementById('logMonitorContainer');
            if (div) {
                setTimeout(() => {
                    let height = div.offsetHeight;
                    let remainder = height % this.itemSize;
                    this.contentHeight = height - remainder;
                    this.padding = remainder;
                    this.virtualScrollViewport?.checkViewportSize();
                });
            }
        }
    }

    private scrollToBottom() {
        setTimeout(() => {
            this.virtualScrollViewport.scrollToIndex(this.currentMessages.length - 1);
        })
    }

    toggleStickToBottom() {
        this.stickToBottom = !this.stickToBottom;
        this.configService.storeCustomData(this.consoleSession.providerType, this.toolbarPluginType, 'stickToBottom', this.stickToBottom);
        this.filterChanged.next();
    }

    logTypeChanged(event: any) {
        this.filter.types = event;
        this.filter = {...this.filter};
        this.configService.storeCustomData(this.consoleSession.providerType, this.toolbarPluginType, 'filter', this.filter);
        this.filterChanged.next();
    }

    messageFilterChanged(event: any) {
        if (event?.length > 0) {
            this.filter.message = event;
            this.filter = {...this.filter};
        } else {
            this.filter.message = undefined;
            this.filter = {...this.filter};
        }
        this.configService.storeCustomData(this.consoleSession.providerType, this.toolbarPluginType, 'filter', this.filter);
        this.filterChanged.next();
    }

    tagFilterChanged(event: any) {
        if (event?.length > 0) {
            this.filter.tag = event;
            this.filter = {...this.filter};
        } else {
            this.filter.tag = undefined;
            this.filter = {...this.filter};
        }
        this.configService.storeCustomData(this.consoleSession.providerType, this.toolbarPluginType, 'filter', this.filter);
        this.filterChanged.next();
    }

    processFilterChanged(event: any) {
        if (event?.length > 0) {
            this.filter.process = event;
            this.filter = {...this.filter};
        } else {
            this.filter.process = undefined;
            this.filter = {...this.filter};
        }
        this.configService.storeCustomData(this.consoleSession.providerType, this.toolbarPluginType, 'filter', this.filter);
        this.filterChanged.next();
    }

    fromTimeFilterChanged(event: any) {
        this.filter.fromTime = event;
        this.filter = {...this.filter};
        this.configService.storeCustomData(this.consoleSession.providerType, this.toolbarPluginType, 'filter', this.filter);
        this.filterChanged.next();
    }

    toTimeFilterChanged(event: any) {
        this.filter.toTime = event;
        this.filter = {...this.filter};
        this.configService.storeCustomData(this.consoleSession.providerType, this.toolbarPluginType, 'filter', this.filter);
        this.filterChanged.next();
    }

    toggleFilter(event: any) {
        this.configService.storeCustomData(this.consoleSession.providerType, this.toolbarPluginType, 'filterEnabled', this.filteringEnabled);
        this.filterChanged.next();
    }

    downloadLog() {
        let text = this.currentMessages.map(m => `${m.type},${m.timestamp},${m.tag ? m.tag + ',' : ''}${m.process ? m.process + ',' : ''}${m.message}`).join('\n');
        let element = document.createElement('a');
        element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text));
        element.setAttribute('download', `log-${this.deviceName}.txt`);

        element.style.display = 'none';
        document.body.appendChild(element);

        element.click();

        document.body.removeChild(element);
    }
}